import React, { useEffect, useState } from 'react';
import { AddonTask } from '@spike/order-model';
import { completeTaskThunk, TasksStatus, uncompleteTaskThunk } from '@spike/tasks-action';
import styles from './AddonTaskCard.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPuzzlePiece } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Spinner from 'components/UI/V3/Spinner';
import useNonInitialEffect from '@versiondos/hooks';
import { reloadOrderThunk } from '@spike/orders-action';

interface AddonTaskCardProps {
    task: AddonTask;
    readOnly?: boolean;
}

export const AddonTaskCard: React.FC<AddonTaskCardProps> = ({ task, readOnly }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();
    const taskStatus = useSelector((state: RootState) => state.tasks.status);
    const [isLoading, setIsLoading] = useState(false);
    const isReadOnly = readOnly ?? false;

    useEffect(() => {
        switch (taskStatus) {
            case TasksStatus.Completing:
            case TasksStatus.Uncompleting:
                setIsLoading(true);
                break;
            case TasksStatus.CompleteSuccess:
            case TasksStatus.UncompleteSuccess:
            case TasksStatus.Error:
                setIsLoading(false);
                break;
        }
    }, [taskStatus]);

    useNonInitialEffect(() => {
        switch (taskStatus) {
            case TasksStatus.CompleteSuccess:
            case TasksStatus.UncompleteSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
        }
    }, [taskStatus]);

    const isCompleted = task.status === 'completed' || !!task.completed;

    const handleCompleteTask = () => {
        if (isReadOnly) return;

        if (isCompleted) {
            dispatch(uncompleteTaskThunk(apiClientWrapper, task.id));
        } else {
            dispatch(completeTaskThunk(apiClientWrapper, task.id));
        }
    };

    return (
        <div className={styles.taskCard}>
            <div className={styles.timeBanner}>
                <span className={styles.taskTime}>
                    {task.taskDate?.clone()?.tz(timezone)?.format('h:mma').toLowerCase() ?? ''}
                </span>
            </div>

            <div className={styles.taskContent}>
                <div className={styles.taskHeader}>
                    <div className={styles.taskMain}>
                        <div className={styles.taskIconContainer}>
                            <FontAwesomeIcon
                                icon={faPuzzlePiece}
                                color="#fff"
                                className={styles.taskIcon}
                            />
                        </div>
                        <div className={styles.taskInfo}>
                            <div className={styles.taskTitle}>{task.addonName}</div>
                            <div className={styles.taskSubtitle}>Add-On</div>
                        </div>
                    </div>
                    <div className={styles.icons}>
                        {isLoading ? (
                            <Spinner size={24} />
                        ) : (
                            !isReadOnly &&
                            (isCompleted ? (
                                <div
                                    className={styles.iconContainer}
                                    onClick={handleCompleteTask}
                                >
                                    <FontAwesomeIcon
                                        icon={faRotateLeft}
                                        color="#000000"
                                        className={styles.icon}
                                    />
                                </div>
                            ) : (
                                <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    color="#92B4A7"
                                    className={styles.completeTaskIcon}
                                    onClick={handleCompleteTask}
                                />
                            ))
                        )}
                    </div>
                </div>

                <div className={styles.separator} />

                <div className={styles.staffSection}>
                    <div className={styles.staffLabel}>Staff</div>
                    <div className={styles.staffName}>{task.staffName || '—'}</div>
                </div>
            </div>
        </div>
    );
};

export default AddonTaskCard;
